import { useState, useEffect, useMemo } from 'react';
import { usePlayerData, gymFocusFor } from './PlayerDataContext';
import { getScheduleDay, getNextMatch } from '../../api';
import { getStatus } from '../GPSData';
import { MATCH_TARGETS } from '../gps/matchChronicShared';

// Derives the full "Day spine v2" view model (design handoff option 7a) from data the app
// already has. Two things the mockup shows are NOT available anywhere in HeraLabX's data and
// are deliberately adapted rather than faked:
//  1. Exact session clock times ("Pitch 09:30", "Gym 16:00") — the week schedule only stores
//     an AM/PM session TYPE (session_am_type/session_pm_type = 'pitch'|'gym'|'none'), never a
//     clock time. Rendered as "Pitch (AM)" / "Gym (PM)" instead of a fabricated time.
//  2. A past-day "still open" task (the mockup's "Gym RPE · Monday — open since yesterday").
//     Players can only submit wellness/RPE against *today* (src/pages/player/PlayerWellness.js
//     has no date field) — there's no backfill flow. The task spine below is therefore
//     today-only; a missed prior day shows up in the 7-day timeline as a dashed "LOG" badge
//     that the player can see but can't retroactively fill from here.
// Also not tracked anywhere: match score (no result/score column on gps_sessions or any other
// table) — match items show opponent + minutes played, never a scoreline.

const DOW_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const DOW_LONG  = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// LOCAL calendar date, not UTC — toISOString() converts to UTC first, which silently rolls
// the date back by one for any positive UTC offset (e.g. Netherlands/CEST) whenever the
// source Date was built at local midnight, making "today" render as yesterday.
function dateStr(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(base, n) { const d = new Date(base); d.setDate(d.getDate() + n); return d; }

function fmtSleep(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

export default function useDaySpineData() {
  const {
    loading: ctxLoading, playerRecord, pid, todayStr,
    rawWellness, rawRpe, rawPlayers, mySessions, todaySchedule,
    todayGymFocus, weeklyHMLD, weeklyHMLDTarget,
  } = usePlayerData();

  const [scheduleByDate, setScheduleByDate] = useState({});
  const [nextMatch, setNextMatch] = useState(null);
  const [fetching, setFetching] = useState(true);

  const dates = useMemo(() => {
    if (!todayStr) return [];
    const base = new Date(todayStr + 'T00:00:00');
    return Array.from({ length: 7 }, (_, i) => dateStr(addDays(base, -i)));
  }, [todayStr]);

  useEffect(() => {
    if (!dates.length) return;
    let cancelled = false;
    setFetching(true);
    Promise.all([
      Promise.all(dates.map(d => getScheduleDay(d).catch(() => null))),
      getNextMatch().catch(() => null),
    ]).then(([days, nm]) => {
      if (cancelled) return;
      const map = {};
      dates.forEach((d, i) => { map[d] = days[i]; });
      setScheduleByDate(map);
      setNextMatch(nm);
    }).finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [dates.join(',')]); // eslint-disable-line

  return useMemo(() => {
    if (ctxLoading || fetching || !pid || !dates.length) {
      return { loading: true };
    }

    const firstName = (playerRecord?.name || '').split(' ')[0] || 'there';
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

    const sessionsByDate = {};
    mySessions.forEach(s => { (sessionsByDate[s.date] ||= []).push(s); });

    const wellnessByDate = {};
    rawWellness.filter(w => String(w.player_id) === pid).forEach(w => {
      wellnessByDate[(w.date || '').slice(0, 10)] = w;
    });

    const rpeByDate = {};
    rawRpe.filter(r => String(r.player_id) === pid).forEach(r => {
      const d = (r.date || '').slice(0, 10);
      const type = r.session_type === 'gym' ? 'gym' : 'pitch';
      (rpeByDate[d] ||= {})[type] = r;
    });

    // Prefer the individualized target (sessionTargets, from PlayerDataContext's mySessions —
    // now resolved through /api/targets/resolve-batch, see the 2026-09-01 individualization
    // pass) over schedule_day's team-wide, position-collapsed snapshot — that snapshot is a
    // coach-facing weekly planning aid (see weekSchedule.js's own header comment), never
    // player-specific, so it's now only a fallback for the rare case where per-player
    // resolution comes back null (e.g. no scenario at all covers this date).
    const zoneFor = (val, sched, key, sessionTargets) => {
      const band = sessionTargets?.[key.toUpperCase()] || (
        sched?.[`${key}_low`] != null && sched?.[`${key}_high`] != null
          ? { lower: Number(sched[`${key}_low`]), upper: Number(sched[`${key}_high`]) }
          : null
      );
      return band ? getStatus(val, band) : 'none';
    };

    // Match items use Match Performance's own target + color method instead of the 4-zone
    // system above: green if the pro-rated-for-minutes-played target was reached, otherwise
    // no color at all (not a 4-way over/in/close/under read) — same math as
    // computeMatchPerformanceRows' atLeast()/pct() in src/pages/gps/matchPerformanceShared.js.
    // Match-day targets are scenario-independent — always the single MATCH_TARGETS
    // table for the position, regardless of which training scenario is assigned.
    const matchTargetFor = (pos) => (pos ? MATCH_TARGETS[pos] || null : null);
    // Targets pro-rate DOWN for a short outing but never UP past the 90-min target —
    // stoppage/extra time shouldn't raise the bar a player is judged against.
    const atLeastTarget = (actual, target, mins) =>
      target != null && mins > 0 && actual >= target * (Math.min(mins, 90) / 90);

    const rpeVal = r => {
      if (!r) return null;
      if (r.session_type === 'gym') return r.rpe_muscular != null ? Math.round(r.rpe_muscular) : null;
      const vals = [r.rpe_muscular, r.rpe_cardiovascular].filter(v => v != null);
      return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
    };

    // ── 7-day timeline ────────────────────────────────────────────────────────
    let sleepSum = 0, sleepCount = 0, rpeSum = 0, rpeCount = 0, sessionCount = 0;

    const days = dates.map((d, i) => {
      const jsDate = new Date(d + 'T00:00:00');
      const sched = scheduleByDate[d];
      const sessions = sessionsByDate[d] || [];
      const wellness = wellnessByDate[d];
      const dayRpe = rpeByDate[d] || {};

      const isMatch = !!sched?.is_match_day;
      const mdLabel = sched?.md_type || null;
      const isToday = i === 0;

      const sleepMin = wellness?.sleep_hours != null ? Math.round(Number(wellness.sleep_hours) * 60) : null;
      if (sleepMin != null) { sleepSum += sleepMin; sleepCount += 1; }

      // Real (GPS-tracked) pitch/match items.
      const items = sessions.map(s => {
        const rpe = dayRpe.pitch;
        const val = rpeVal(rpe);
        if (val != null) { rpeSum += val; rpeCount += 1; }
        sessionCount += 1;
        const isMatchItem = isMatch;
        const mTarget = isMatchItem ? matchTargetFor(s.position) : null;
        return {
          key: `s-${s.sessionId}`,
          sessionId: s.sessionId,
          type: isMatchItem ? 'match' : 'pitch',
          name: isMatchItem ? (s.title || 'Match') : (s.title || 'Training'),
          meta: isMatchItem
            ? `${sched?.home_away ? (sched.home_away === 'home' ? 'Home' : 'Away') + ' · ' : ''}${s.minutes_played ? `${Math.round(s.minutes_played)} min played` : 'Match day'}`
            : (s.minutes_played ? `${Math.round(s.minutes_played)} min` : 'Training session'),
          rpe: val, rpeFilled: val != null,
          metrics: isMatchItem
            ? [
                { key: 'td', label: 'TD', val: s.td, unit: 'm', zone: atLeastTarget(s.td, mTarget?.TD, s.minutes_played) ? 'green' : 'none' },
                { key: 'hid', label: 'HID', val: s.hid, unit: 'm', zone: atLeastTarget(s.hid, mTarget?.HID, s.minutes_played) ? 'green' : 'none' },
                { key: 'hmld', label: 'HMLD', val: s.hmld, unit: 'm', zone: atLeastTarget(s.hmld, mTarget?.HMLD, s.minutes_played) ? 'green' : 'none' },
              ]
            : [
                { key: 'td', label: 'TD', val: s.td, unit: 'm', zone: zoneFor(s.td, sched, 'td', s.targets) },
                { key: 'hid', label: 'HID', val: s.hid, unit: 'm', zone: zoneFor(s.hid, sched, 'hid', s.targets) },
                { key: 'hmld', label: 'HMLD', val: s.hmld, unit: 'm', zone: zoneFor(s.hmld, sched, 'hmld', s.targets) },
              ],
        };
      });

      // Synthetic gym item — gym sessions aren't GPS-tracked, so this only exists when this
      // player was actually assigned gym that day (per the coach's per-player Gym breakdown,
      // see gymFocusFor) or a gym RPE was actually logged for the day (real signals either
      // way, never fabricated).
      const dayGymFocus = gymFocusFor(sched, pid);
      if (dayGymFocus.assigned || dayRpe.gym) {
        const val = rpeVal(dayRpe.gym);
        if (val != null) { rpeSum += val; rpeCount += 1; }
        sessionCount += 1;
        const accentParts = [];
        if (dayGymFocus.upper) accentParts.push('Upper body');
        if (dayGymFocus.lower) accentParts.push('Lower body');
        if (dayGymFocus.other) accentParts.push('Other');
        items.push({
          key: `gym-${d}`, type: 'gym', name: 'Gym session',
          meta: accentParts.length ? accentParts.join(' + ') : 'Strength & conditioning',
          rpe: val, rpeFilled: val != null, metrics: [],
        });
      }

      return {
        date: d,
        dow: DOW_SHORT[jsDate.getDay()],
        dom: jsDate.getDate(),
        title: isToday ? 'Today' : DOW_LONG[jsDate.getDay()],
        isToday,
        tag: isMatch ? 'MATCH' : (mdLabel || 'REST'),
        tagKind: isMatch ? 'match' : (isToday ? 'today' : 'neutral'),
        sleepMin,
        items,
      };
    });

    const avgSleep = sleepCount ? Math.round(sleepSum / sleepCount) : null;
    const avgRpe = rpeCount ? Math.round((rpeSum / rpeCount) * 10) / 10 : null;

    // ── Task spine (today only — see file header) ───────────────────────────
    // The row can exist with submitted_at NULL (created by the Oura sync to hold sleep data) —
    // that's sleep data, not a submitted check-in.
    const todayWellnessRow = wellnessByDate[todayStr];
    const todayWellness = todayWellnessRow?.submitted_at ? todayWellnessRow : null;
    const todayPitchRpe = rpeByDate[todayStr]?.pitch;
    const todayGymRpe = rpeByDate[todayStr]?.gym;
    const gymOpenToday = todayGymFocus.assigned && !todayGymRpe;
    const pitchPlannedToday = todaySchedule?.session_am_type === 'pitch' || todaySchedule?.session_pm_type === 'pitch' || !!todaySchedule?.is_match_day;

    const tasks = [];
    tasks.push({
      key: 'wellness', kind: 'wellness', path: '/player/forms/wellness',
      submitted: !!todayWellness,
      when: todayWellness ? `SUBMITTED · ${new Date(todayWellness.submitted_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : 'MORNING · BY 08:30',
      title: 'Wellness check',
      sub: todayWellness ? 'Thanks — staff have it.' : 'Sleep, soreness and mood. Takes 30 seconds.',
      cta: todayWellness ? 'Submitted' : 'Submit now',
    });
    // Unlike PlayerDataContext.js's rpeForms.pitch (unconditionally true, so other screens
    // keep collecting the metric even on an unplanned day), the task spine only asks for
    // Pitch RPE when the week schedule actually planned a pitch session (or match) today —
    // no point nagging a player to rate a session that didn't happen. Same shape as the gym
    // block below: still shows the card in its submitted state if one was logged anyway.
    if (pitchPlannedToday || todayPitchRpe) {
      tasks.push({
        key: 'pitch-rpe', kind: 'rpe', path: '/player/forms/rpe',
        submitted: !!todayPitchRpe,
        when: todayPitchRpe ? `SUBMITTED · ${new Date(todayPitchRpe.submitted_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : "OPEN · TODAY'S PITCH SESSION",
        title: 'Pitch RPE',
        sub: todayPitchRpe ? 'Rating sent to staff.' : 'Pitch session still unrated.',
        cta: todayPitchRpe ? 'Submitted' : 'Rate pitch session',
      });
    }
    if (gymOpenToday || todayGymRpe) {
      const gymAccentParts = [];
      if (todayGymFocus.upper) gymAccentParts.push('Upper body');
      if (todayGymFocus.lower) gymAccentParts.push('Lower body');
      if (todayGymFocus.other) gymAccentParts.push('Other');
      const gymAccent = gymAccentParts.join(' + ');
      tasks.push({
        key: 'gym-rpe', kind: 'rpe', path: '/player/forms/rpe-gym',
        submitted: !!todayGymRpe,
        when: todayGymRpe ? `SUBMITTED · ${new Date(todayGymRpe.submitted_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : "OPEN · TODAY'S GYM SESSION",
        title: gymAccent ? `Gym RPE · ${gymAccent}` : 'Gym RPE',
        sub: todayGymRpe ? 'Rating sent to staff.' : (gymAccent ? `${gymAccent} — strength block still unrated.` : 'Strength block still unrated.'),
        cta: todayGymRpe ? 'Submitted' : 'Rate gym session',
      });
    }

    // ── Hera brief (deterministic template, per handoff's implementation note) ──
    const scheduleTags = [];
    if (todaySchedule?.session_am_type === 'pitch' || todaySchedule?.session_pm_type === 'pitch') {
      scheduleTags.push({ label: 'Pitch (AM)', bg: 'rgba(63,108,245,.1)', fg: '#2b4fbd', dot: '#3f6cf5' });
    }
    if (todayGymFocus.assigned) {
      const accentParts = [];
      if (todayGymFocus.upper) accentParts.push('Upper body');
      if (todayGymFocus.lower) accentParts.push('Lower body');
      if (todayGymFocus.other) accentParts.push('Other');
      const gymLabel = accentParts.length ? `Gym · ${accentParts.join(' + ')} (PM)` : 'Gym (PM)';
      scheduleTags.push({ label: gymLabel, bg: 'rgba(106,91,214,.1)', fg: '#4c3fa8', dot: '#6a5bd6' });
    }
    const matchTomorrow = nextMatch && dateStr(addDays(new Date(todayStr + 'T00:00:00'), 1)) === (nextMatch.match_date || '').slice(0, 10);
    if (matchTomorrow) {
      scheduleTags.push({ label: 'Match tomorrow', bg: 'rgba(226,98,47,.1)', fg: '#c2481a', dot: '#e2622f' });
    }

    const openCount = tasks.filter(t => !t.submitted).length;
    // "Last night's sleep" is only ever today's wellness row (today's sleep_hours *is* the
    // night leading into today) — days[1] (yesterday's row) describes the night before
    // *that*, two nights back. Quoting it while labelled "last night" is what caused the
    // brief ("6h30") to visibly disagree with the timeline's real today-row number ("9h")
    // whenever wellness was already submitted but another task (e.g. RPE) was still open.
    // So: only quote a number when it's genuinely today's, otherwise say nothing about sleep.
    const todaySleep = todayWellnessRow ? days[0]?.sleepMin : null;
    const matchLine = matchTomorrow
      ? ` Match tomorrow ${nextMatch.is_home ? 'at home' : 'away'} against ${nextMatch.opponent}, so tonight's sleep counts as training.`
      : '';
    const scheduleLine = scheduleTags.length
      ? ` ${scheduleTags.map(t => t.label).join(' · ')} today.`
      : '';

    let brief;
    if (openCount === 0) {
      brief = `All caught up — nothing waiting on you.${scheduleLine}${matchLine}`.trim();
    } else {
      const openTitles = tasks.filter(t => !t.submitted).map(t => t.title.toLowerCase());
      const sleepNote = todaySleep != null ? ` You slept ${fmtSleep(todaySleep)} last night.` : '';
      brief = `Still open: ${openTitles.join(' and ')}.${sleepNote}${scheduleLine}${matchLine}`.trim();
    }

    const squadSize = rawPlayers.filter(p => p.active).length;
    const weekPct = weeklyHMLDTarget > 0 ? Math.round((weeklyHMLD / weeklyHMLDTarget) * 100) : 0;

    return {
      loading: false,
      firstName, timeOfDay,
      eyebrow: `${DOW_LONG[new Date(todayStr + 'T00:00:00').getDay()].toUpperCase()} ${new Date(todayStr + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase()}${todaySchedule?.md_type ? ` · ${todaySchedule.md_type}` : ''}`,
      brief, briefTags: scheduleTags,
      tasks,
      days,
      dateRangeLabel: `${new Date(dates[6] + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${new Date(dates[0] + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`,
      avgSleep, avgRpe, sessionCount,
      weekPct, weeklyHMLD, weeklyHMLDTarget,
      squadSize,
    };
  }, [ctxLoading, fetching, pid, dates, playerRecord, mySessions, rawWellness, rawRpe, rawPlayers, scheduleByDate, todaySchedule, todayGymFocus, todayStr, nextMatch, weeklyHMLD, weeklyHMLDTarget]);
}
